/**
 * Renders the default color palettes for quick selection.
 */
import { CheckCircleBox } from '@/components/blocks/ProductDetails/parts/buyByColor/CheckCircleBox';
import {
	colorPaletteSelectColorRenderColorSx,
	colorPaletteSlicedGridSx,
	colorPaletteTypographyNameRenderColorSx,
	colorsPaletteContainerSx,
	colorsPaletteGridRenderColorSx,
	colorsPaletteTitleSx,
} from '@/components/blocks/ProductDetails/styles/colorsPaletteDefault';
import { paintColorsPalette } from '@/data/constants/paintColorPalette';
import { useLocalization } from '@/data/Localization';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { LineRangeColor } from '@/data/types/ColorType';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
interface ColorsPaletteDefaultProps {
	colorRangeSelected?: string;
	isDrawer?: boolean;
	allLineColors: LineRangeColor[] | undefined;
	rangeLinkHandler?: () => void;
}

export const ColorsPaletteDefault: FC<ColorsPaletteDefaultProps> = (props) => {
	const { colorRangeSelected, isDrawer, allLineColors, rangeLinkHandler } = props;
	const [colorsPalette, setColorsPalette] = useState<typeof paintColorsPalette | null>(null);
	const { colorDrawerState, actions: colorDrawerActions } = useColorDrawerState();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const HDMProductDetail = useLocalization('HDMProductDetail');

	useEffect(() => {
		setColorsPalette(paintColorsPalette.filter((color) => color.name !== '#N/D'));
	}, []);

	const handleSelectColor = useCallback(
		(rangeName: string) => {
			const selectedRangeColors = allLineColors?.filter(
				(lineColor) => lineColor.range === rangeName
			)[0];

			colorDrawerActions.setColorDrawerData({
				rangeColors: selectedRangeColors?.colors,
				pendingRange: rangeName,
			});
			// Trigger global state to open the drawer
			colorDrawerActions.openBuyByColorDrawer(rangeName);
		},
		[allLineColors, colorDrawerActions]
	);

	const renderColor = useCallback(
		({ name, color }: { name: string; color: string }) => {
			const isSelected =
				(isDrawer && name === colorRangeSelected) ||
				(!isDrawer && !colorDrawerState?.isOpen && name === colorRangeSelected);

			return (
				<Grid item key={name} sx={colorsPaletteGridRenderColorSx}>
					<Box
						onClick={() => handleSelectColor(name)}
						sx={colorPaletteSelectColorRenderColorSx({ isMobile, color })}
					>
						{isSelected ? <CheckCircleBox isMobile={isMobile} /> : null}
					</Box>
					{isDrawer ? (
						<Typography variant="bodyCaption" sx={colorPaletteTypographyNameRenderColorSx}>
							{name}
						</Typography>
					) : null}
				</Grid>
			);
		},
		[isDrawer, colorDrawerState?.isOpen, colorRangeSelected, handleSelectColor, isMobile]
	);

	if (!colorsPalette) return null;

	return (
		<Box sx={colorsPaletteContainerSx}>
			<Box sx={colorsPaletteTitleSx}>
				<Typography variant="body1" fontWeight={OpenSansFontWeights.semibold} color="grey.500">
					{HDMProductDetail.BuyByColor.labels.familyColors.t()}
				</Typography>
				<Typography onClick={rangeLinkHandler} color="secondary.main">
					{isDrawer ? colorDrawerState?.pendingRange : colorDrawerState?.range}
				</Typography>
			</Box>

			{isMobileOrTablet || isDrawer ? (
				<Box>
					<Grid container justifyContent="space-between" sx={colorPaletteSlicedGridSx}>
						{colorsPalette.slice(0, 5).map(renderColor)}
					</Grid>
					<Grid container justifyContent="space-between">
						{colorsPalette.slice(5, 10).map(renderColor)}
					</Grid>
				</Box>
			) : (
				<Grid container justifyContent="space-between">
					{colorsPalette.map(renderColor)}
				</Grid>
			)}
		</Box>
	);
};
